import type { ReactNode } from 'react'
import { formatAngka, formatRupiah, formatTanggalJam, labelStatus } from '@/lib/format'
import { Card } from './Card'
import { ErrorState } from './ErrorState'
import { SkeletonKartu, SkeletonTabel } from './Skeleton'

/**
 * Perender laporan generik.
 *
 * Endpoint laporan mengembalikan bentuk JSON yang berbeda-beda per laporan
 * (kadang { total..., baris: [...] }, kadang { pendapatan: [...], beban: [...] }).
 * Alih-alih menebak satu bentuk, `rangkaiLaporan()` menelusuri payload dan
 * memecahnya menjadi kartu angka + tabel bagian, lalu dirender apa adanya.
 * Dengan begitu setiap angka yang tampil BENAR-BENAR berasal dari API.
 */

export interface BagianLaporan {
  judul: string
  kolom: string[]
  baris: Array<Record<string, unknown>>
}

export interface KartuLaporan {
  label: string
  nilai: number | string
}

export interface LaporanTernormalisasi {
  kartu: KartuLaporan[]
  bagian: BagianLaporan[]
}

const KUNCI_UANG =
  /(amount|nominal|saldo|total|nilai|debit|kredit|biaya|harga|anggaran|realisasi|sisa|penerimaan|pengeluaran|aset|liabilitas|pendapatan|beban|surplus|defisit|outstanding|subtotal|paid|paidAmount|balance|jumlah)/i

function judulDariKunci(kunci: string): string {
  return kunci
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function nilaiTampil(kunci: string, nilai: unknown): ReactNode {
  if (nilai === null || nilai === undefined || nilai === '') {
    return <span className="text-netral-400">-</span>
  }
  if (typeof nilai === 'number') {
    if (KUNCI_UANG.test(kunci)) return <span className="angka-tabular">{formatRupiah(nilai)}</span>
    return <span className="angka-tabular">{formatAngka(nilai, Number.isInteger(nilai) ? 0 : 2)}</span>
  }
  if (typeof nilai === 'boolean') return nilai ? 'Ya' : 'Tidak'
  if (typeof nilai === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(nilai)) return formatTanggalJam(nilai)
    if (/^(ACTIVE|DRAFT|POSTED|APPROVED|REJECTED|PAID|UNPAID|OVERDUE|CANCELLED|ASSET|LIABILITY|EQUITY|REVENUE|EXPENSE)$/.test(nilai)) {
      return labelStatus(nilai)
    }
    return nilai
  }
  if (Array.isArray(nilai)) return `${nilai.length} baris`
  return JSON.stringify(nilai)
}

export function rangkaiLaporan(data: unknown, kedalaman = 0, prefiks = ''): LaporanTernormalisasi {
  const kartu: KartuLaporan[] = []
  const bagian: BagianLaporan[] = []

  if (data === null || data === undefined) return { kartu, bagian }

  if (Array.isArray(data)) {
    const objek = data.filter((x) => x !== null && typeof x === 'object') as Array<Record<string, unknown>>
    if (data.length > 0 && objek.length === data.length) {
      bagian.push({ judul: prefiks || 'Data', kolom: kunciGabungan(objek), baris: objek })
    } else if (data.length > 0) {
      bagian.push({
        judul: prefiks || 'Data',
        kolom: ['Nilai'],
        baris: data.map((v) => ({ Nilai: v as unknown })),
      })
    }
    return { kartu, bagian }
  }

  if (typeof data !== 'object') {
    kartu.push({ label: prefiks || 'Nilai', nilai: data as number | string })
    return { kartu, bagian }
  }

  for (const [kunci, nilai] of Object.entries(data as Record<string, unknown>)) {
    if (kunci === 'meta' || kunci === 'success') continue
    const judul = prefiks ? `${prefiks} — ${judulDariKunci(kunci)}` : judulDariKunci(kunci)

    if (nilai === null || nilai === undefined) continue

    if (typeof nilai === 'number' || typeof nilai === 'string' || typeof nilai === 'boolean') {
      if (typeof nilai === 'string' && nilai.length > 120) continue
      kartu.push({ label: judul, nilai: nilai as number | string })
      continue
    }

    if (Array.isArray(nilai)) {
      if (nilai.length === 0) continue
      const objek = nilai.filter((x) => x !== null && typeof x === 'object') as Array<Record<string, unknown>>
      if (objek.length === nilai.length) {
        bagian.push({ judul, kolom: kunciGabungan(objek), baris: objek })
      } else {
        bagian.push({ judul, kolom: ['Nilai'], baris: nilai.map((v) => ({ Nilai: v as unknown })) })
      }
      continue
    }

    if (typeof nilai === 'object' && kedalaman < 2) {
      const dalam = rangkaiLaporan(nilai, kedalaman + 1, judul)
      kartu.push(...dalam.kartu)
      bagian.push(...dalam.bagian)
    }
  }

  return { kartu, bagian }
}

function kunciGabungan(baris: Array<Record<string, unknown>>): string[] {
  const kunci: string[] = []
  for (const b of baris) {
    for (const k of Object.keys(b)) {
      if (!kunci.includes(k) && typeof b[k] !== 'object') kunci.push(k)
      if (kunci.length >= 10) return kunci
    }
  }
  return kunci.length > 0 ? kunci : ['Nilai']
}

export function jumlahKolom(bagian: BagianLaporan, kolom: string): number {
  return bagian.baris.reduce((a, b) => {
    const v = b[kolom]
    return a + (typeof v === 'number' ? v : 0)
  }, 0)
}

export function LaporanTampil({
  data,
  memuat,
  galat,
  kodeGalat,
  onCoba,
  kosong,
}: {
  data: unknown
  memuat: boolean
  galat: string | null
  kodeGalat?: string | null
  onCoba?: () => void
  kosong?: ReactNode
}) {
  if (galat) return <ErrorState pesan={galat} kode={kodeGalat} onCoba={onCoba} />
  if (memuat) {
    return (
      <div className="space-y-4">
        <SkeletonKartu jumlah={4} />
        <SkeletonTabel />
      </div>
    )
  }

  const { kartu, bagian } = rangkaiLaporan(data)

  if (kartu.length === 0 && bagian.length === 0) {
    return (
      <Card>
        {kosong ?? <p className="py-6 text-center text-sm text-netral-500">Tidak ada data pada periode ini.</p>}
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {kartu.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {kartu.map((k) => (
            <div key={k.label} className="rounded-xl border border-netral-200 bg-white p-3.5">
              <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{k.label}</p>
              <p className="angka-tabular mt-1.5 text-lg font-semibold break-words text-netral-900">
                {nilaiTampil(k.label, k.nilai)}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {bagian.map((b) => (
        <Card key={b.judul} padat className="overflow-hidden">
          <div className="border-b border-netral-200 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-netral-800">{b.judul}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-netral-50 text-left text-xs font-semibold tracking-wide text-netral-600 uppercase">
                <tr>
                  {b.kolom.map((k) => (
                    <th key={k} scope="col" className="px-3 py-2 whitespace-nowrap">
                      {judulDariKunci(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-netral-100">
                {b.baris.map((baris, i) => (
                  <tr key={i} className="hover:bg-netral-50">
                    {b.kolom.map((k) => (
                      <td key={k} className="px-3 py-2 align-top">
                        {nilaiTampil(k, baris[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  )
}

/** Mencari nilai numerik pertama dari daftar kunci alternatif. */
export function cariAngka(data: unknown, kunci: string[]): number | null {
  if (data === null || typeof data !== 'object') return null
  const obj = data as Record<string, unknown>
  for (const k of kunci) {
    const v = obj[k]
    if (typeof v === 'number') return v
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v)
  }
  return null
}

/** Mencari nilai apa pun (termasuk objek/array) dari daftar kunci alternatif. */
export function cariNilai(data: unknown, kunci: string[]): unknown {
  if (data === null || typeof data !== 'object') return undefined
  const obj = data as Record<string, unknown>
  for (const k of kunci) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k]
  }
  return undefined
}

/**
 * Mengambil daftar baris dari payload laporan. Bila `data` sendiri berupa array,
 * langsung dipakai; bila objek, dicari pada kunci alternatif yang diberikan.
 */
export function ambilBaris(data: unknown, kandidat: string[]): Array<Record<string, unknown>> {
  if (Array.isArray(data)) {
    return data.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null)
  }
  if (data === null || typeof data !== 'object') return []
  const obj = data as Record<string, unknown>
  for (const k of kandidat) {
    const v = obj[k]
    if (Array.isArray(v)) {
      return v.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null)
    }
  }
  return []
}

/** Membaca angka dari nilai apa pun (number atau string numerik). */
export function keAngkaAman(nilai: unknown): number | null {
  if (typeof nilai === 'number') return Number.isFinite(nilai) ? nilai : null
  if (typeof nilai === 'string') {
    const n = Number(nilai.replace(/[^0-9.-]/g, ''))
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Membaca bidang numerik pertama yang tersedia dari sebuah baris. */
export function angkaBaris(baris: Record<string, unknown>, kandidat: string[]): number {
  for (const k of kandidat) {
    const n = keAngkaAman(baris[k])
    if (n !== null) return n
  }
  return 0
}
